import { History } from 'lucide-react';
import type { Capacity } from '@devrobot/shared';
import { formatBytes, percent } from '../lib/format';

function Bar({ label, used, total, detail }: { label: string; used: number; total: number; detail: string }) {
  const p = percent(used, total);
  const cls = p >= 90 ? 'progress-error' : p >= 75 ? 'progress-warning' : '';
  const ink = p >= 90 ? 'font-semibold dr-ink-error' : p >= 75 ? 'font-semibold dr-ink-warning' : '';
  return (
    <div className="min-w-28">
      <div className="mb-1.5 flex justify-between gap-2 text-xs">
        <span className="text-base-content/65">{label}</span>
        <span className={`tabular-nums ${ink}`}>{p}%</span>
      </div>
      <progress className={`progress h-1.5 w-full ${cls}`} value={p} max={100} aria-label={`${label} ${p}% used`} />
      <div className="mt-1 text-xs text-base-content/55 tabular-nums">{detail}</div>
    </div>
  );
}

/** CPU count and memory/disk utilisation bars. `stale` labels the numbers as last known. */
export function CapacityBars({ capacity, stale, compact }: { capacity: Capacity | null; stale?: boolean; compact?: boolean }) {
  if (!capacity) return <span className="text-sm text-base-content/60">not observed</span>;
  const memUsed = capacity.memoryBytes - capacity.memoryAvailableBytes;
  const diskUsed = capacity.diskTotalBytes - capacity.diskFreeBytes;
  return (
    <div className={`grid gap-3 ${compact ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-3'}`}>
      {!compact ? (
        <div>
          <div className="text-xs text-base-content/65">CPU</div>
          <div className="text-lg leading-tight font-semibold tabular-nums">{capacity.cpus}</div>
          <div className="text-xs text-base-content/55">cores</div>
        </div>
      ) : null}
      <Bar label={compact ? `Mem · ${capacity.cpus} CPU` : 'Memory'} used={memUsed} total={capacity.memoryBytes} detail={`${formatBytes(memUsed)} / ${formatBytes(capacity.memoryBytes)}`} />
      <Bar label="Disk" used={diskUsed} total={capacity.diskTotalBytes} detail={`${formatBytes(capacity.diskFreeBytes)} free`} />
      {stale ? (
        <div className={`text-xs text-base-content/65 ${compact ? '' : 'sm:col-span-3'}`}>
          <span className="badge badge-soft badge-warning badge-xs mr-1 gap-1">
            <History className="size-2.5" aria-hidden="true" /> stale
          </span>
          Last known values, not live.
        </div>
      ) : null}
    </div>
  );
}
